import type { ZodType } from 'zod';
import { reportApiFailure } from '@/monitoring/report';

export const API_BASE = '/api/v1';

/**
 * A hung request must fail loudly: the wallet store only re-arms its poll after `refresh()`
 * resolves (see apps/web/src/stores/wallet.ts), so a connection that never answers would
 * otherwise stall polling forever. 15s comfortably covers slow mobile networks.
 */
export const REQUEST_TIMEOUT_MS = 15_000;

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly detail: string,
    readonly extra: Record<string, unknown> = {},
    options?: ErrorOptions,
  ) {
    super(detail, options);
    this.name = 'ApiError';
  }
}

/** The one place stores turn a caught error into the code driving their `error` state. */
export function toErrorCode(error: unknown): string {
  return error instanceof ApiError ? error.code : 'generic';
}

export interface RequestOptions<T> {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body?: unknown;
  token?: string;
  schema: ZodType<T>;
  timeoutMs?: number;
}

/** Correlation keys from the response: the API's request id (common/request-id.ts) and Cloudflare's ray id. */
function responseMeta(response: Response): Record<string, string> {
  const meta: Record<string, string> = {};
  const requestId = response.headers.get('x-request-id');
  const cfRay = response.headers.get('cf-ray');
  if (requestId) meta.requestId = requestId;
  if (cfRay) meta.cfRay = cfRay;
  return meta;
}

export async function request<T>(options: RequestOptions<T>): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.token) headers.authorization = `Bearer ${options.token}`;
  const path = `${API_BASE}${options.path}`;
  // Every failure leaves through here exactly once, so monitoring sees each one exactly once.
  const fail = (error: ApiError): never => {
    reportApiFailure({ error, method: options.method, path });
    throw error;
  };

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: AbortSignal.timeout(options.timeoutMs ?? REQUEST_TIMEOUT_MS),
    });
  } catch (err) {
    return fail(new ApiError('NETWORK', 0, 'Network error', {}, { cause: err }));
  }

  if (!response.ok) return fail(await toApiError(response));

  let text: string;
  try {
    text = await response.text();
  } catch (err) {
    // The timeout signal also aborts the body read; the headers did arrive, so keep their meta.
    return fail(
      new ApiError('NETWORK', 0, 'Network error', responseMeta(response), { cause: err }),
    );
  }
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    return fail(
      new ApiError(
        'BAD_RESPONSE',
        response.status,
        'Malformed response body',
        responseMeta(response),
      ),
    );
  }
  const parsed = options.schema.safeParse(json);
  if (!parsed.success)
    return fail(
      new ApiError('BAD_RESPONSE', response.status, 'Unexpected response shape', {
        ...responseMeta(response),
        // Paths only: a received value could be anything, including something private.
        issues: parsed.error.issues.map((issue) => issue.path.join('.')),
      }),
    );
  return parsed.data;
}

async function toApiError(response: Response): Promise<ApiError> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('json')) {
    try {
      const problem = (await response.json()) as Record<string, unknown>;
      const { code, status: _s, detail, title: _t, type: _ty, instance: _i, ...extra } = problem;
      if (typeof code === 'string') {
        return new ApiError(code, response.status, typeof detail === 'string' ? detail : code, {
          ...extra,
          ...responseMeta(response),
        });
      }
    } catch {
      // fall through to the generic error
    }
  }
  return new ApiError(
    `HTTP_${response.status}`,
    response.status,
    response.statusText || 'Request failed',
    responseMeta(response),
  );
}
